import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { api } from '../../../lib/api';

type TrafficBucket = { bucket: string; requests: number; unique_visitors: number };
type TrafficPath = { path: string; requests: number };
type TrafficSummary = {
  days: number;
  date_from: string;
  date_to: string;
  total_requests: number;
  unique_visitors: number;
  by_day: TrafficBucket[];
  by_country: TrafficBucket[];
  top_paths: TrafficPath[];
};

const daysOptions = [
  { value: 7, label: '7 zile' },
  { value: 30, label: '30 zile' },
  { value: 90, label: '90 zile' },
  { value: 365, label: '1 an' },
];

const channelOptions = [
  { value: 'all', label: 'Tot' },
  { value: 'web', label: 'Web (non-API)' },
  { value: 'api', label: 'API' },
];

export default function TrafficReportsPanel() {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState<number>(30);
  const [channel, setChannel] = useState<'all' | 'web' | 'api'>('all');
  const [data, setData] = useState<TrafficSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/admin/traffic/summary', { params: { days, channel } });
      setData(resp.data as any);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca traficul.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [channel, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const topCountries = useMemo(() => (data?.by_country || []).slice(0, 10), [data?.by_country]);
  const topPaths = useMemo(() => (data?.top_paths || []).slice(0, 10), [data?.top_paths]);

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-foreground">Trafic site</div>
          <div className="text-sm text-muted-foreground">
            Accesări și țări (best-effort, pe baza headerelor din proxy/CDN).
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            label="Perioadă"
            value={days}
            options={daysOptions as any}
            onChange={(v) => setDays(Number(v))}
          />
          <Select
            label="Canal"
            value={channel}
            options={channelOptions as any}
            onChange={(v) => setChannel(String(v) as any)}
          />
          <Button variant="outline" iconName="RefreshCw" disabled={loading} onClick={() => void load()}>
            Reîncarcă
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {[
          { label: 'Interval', value: data ? `${data.date_from} → ${data.date_to}` : '—', icon: 'Calendar' },
          { label: 'Cereri', value: data ? String(data.total_requests ?? 0) : '—', icon: 'Activity' },
          { label: 'Vizitatori unici', value: data ? String(data.unique_visitors ?? 0) : '—', icon: 'Users' },
          { label: 'Canal', value: channelOptions.find((o) => o.value === channel)?.label || channel, icon: 'Filter' },
        ].map((c) => (
          <div key={c.label} className="border border-border rounded-lg p-3 bg-background">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">{c.label}</div>
              <Icon name={c.icon as any} size={16} className="text-muted-foreground" />
            </div>
            <div className="text-lg font-bold text-foreground mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="border border-border rounded-lg p-3 bg-background lg:col-span-1">
          <div className="font-medium text-foreground mb-2">Top țări</div>
          {topCountries.length === 0 ? (
            <div className="text-sm text-muted-foreground">—</div>
          ) : (
            <div className="space-y-2">
              {topCountries.map((r) => (
                <div key={r.bucket} className="flex items-center justify-between text-sm">
                  <div className="font-mono text-xs text-muted-foreground">{r.bucket || '??'}</div>
                  <div className="text-foreground font-medium">{r.requests}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-border rounded-lg p-3 bg-background lg:col-span-1">
          <div className="font-medium text-foreground mb-2">Top pagini</div>
          {topPaths.length === 0 ? (
            <div className="text-sm text-muted-foreground">—</div>
          ) : (
            <div className="space-y-2">
              {topPaths.map((r) => (
                <div key={r.path} className="flex items-center justify-between gap-3 text-sm">
                  <div className="text-muted-foreground truncate flex-1">{r.path}</div>
                  <div className="text-foreground font-medium">{r.requests}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-border rounded-lg p-3 bg-background lg:col-span-1">
          <div className="font-medium text-foreground mb-2">Pe zile</div>
          {data?.by_day?.length ? (
            <div className="max-h-56 overflow-auto divide-y divide-border">
              {data.by_day.map((r) => (
                <div key={r.bucket} className="py-2 flex items-center justify-between text-sm">
                  <div className="text-muted-foreground font-mono text-xs">{r.bucket}</div>
                  <div className="text-foreground font-medium">{r.requests}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">—</div>
          )}
        </div>
      </div>
    </div>
  );
}

